import type { CadFeatureMap, TriggerEnumType } from '@mark43/cad-api';
import { TriggerEnum } from '@mark43/cad-api';
import {
    colorSoundConfigStore,
    ConfiguredSoundSubject,
} from '../configurable-colors-sounds/color-sound-config-store';
import { URGENT_MESSAGE_PRIORITY_ID } from '../configurable-colors-sounds/color-sound-config-types';
import { electronBridge } from '../electron-bridge/electron-bridge';
import { logErrorMessage } from '../monitoring/log';
import { preloadSound, SoundName, SoundPlayer, soundPlayer } from './play-sound';

/**
 * The CadFeatureMap keys of the CAD Audio Alerts preferences (`PreferenceGroup.AUDIO_ALERTS`). They
 * only apply while configurable colors and sounds is off.
 */
export type AudibleAlertFeatures = Partial<
    Pick<
        CadFeatureMap,
        | 'dispatchPlayAlertOnEnterUnassignedQueue'
        | 'dispatchPlayAlertOnAedEvent'
        | 'dispatchPlayAlertOnNewStandardMessage'
        | 'dispatchPlayAlertOnNewUrgentMessage'
        | 'dispatchPlayAlertOnUnitStatusTimerExpiration'
        | 'playAlertOnHotHitResultForOfficerDexQuery'
        | 'firstResponderPlayAlertOnHotHitDexResult'
        | 'firstResponderPlayAlertOnIncomingAssignment'
        | 'firstResponderPlayAlertOnIncomingAedAssignment'
        | 'firstResponderPlayAlertOnNewStandardMessage'
        | 'firstResponderPlayAlertOnNewUrgentMessage'
        | 'playAlertOnUrgentBolos'
    >
>;

/** Who hears an alert: their Audio Alerts preferences, and whether this is CAD or First Responder. */
export interface AlertContext {
    enabledFeatures: AudibleAlertFeatures | undefined;
    isCad: boolean;
}

type AgencyTypeId = number | null | undefined;

/**
 * Something that happened that may sound. `type` names the alert; the other fields are what the
 * department's configuration and the alert's rules need to pick its sound.
 */
export type AudibleAlert =
    | {
          type: 'NEW_EVENT';
          context: AlertContext;
          isAed: boolean;
          isCallTakenByCurrentUser: boolean;
          priorityId: number | null;
          agencyTypeGlobalAttrId: AgencyTypeId;
      }
    | {
          type: 'AED_ATTEMPTING_ACCEPTING';
          context: AlertContext;
          agencyTypeGlobalAttrId: AgencyTypeId;
      }
    | { type: 'UNIT_TIMER_EXPIRED'; context: AlertContext; agencyTypeGlobalAttrId: AgencyTypeId }
    | { type: 'URGENT_BOLO_RECEIVED'; context: AlertContext }
    | {
          type: 'INCOMING_ASSIGNMENT';
          context: AlertContext;
          isAed: boolean;
          priorityId: number | null | undefined;
          agencyTypeGlobalAttrId: AgencyTypeId;
      }
    | {
          type: 'DEX_HOT_HIT';
          context: AlertContext;
          hasNewHotHitFromOtherUserQuery: boolean;
          hasNewHotHitFromOwnQuery: boolean;
      }
    | { type: 'MESSAGE_RECEIVED'; context: AlertContext; isUrgent: boolean }
    | { type: 'EMERGENCY' }
    | { type: 'DRAFT_EVENT_TIMER_EXPIRED' };

export type AudibleAlertType = AudibleAlert['type'];

export interface AudibleAlerts {
    /**
     * Plays the alert's sound, if it has one here. Returns whether it did, or, in Electron, whether
     * it asked to play in one window. A looping alert keeps looping until `stopAlert`.
     */
    playAlert: (alert: AudibleAlert) => boolean;
    /** Stops whatever this alert is playing, and only that. */
    stopAlert: (type: AudibleAlertType) => void;
}

/**
 * Each alert type plays on its own channel, so one alert's debounce or stop never reaches another
 * alert, even one configured with the same sound. With configurable sounds off, each alert plays
 * only its default sound and no two alert types share one, so a channel is exactly the per-sound
 * debounce and stop every alert had before configurable sounds.
 */
const DEFAULT_SOUNDS: Record<AudibleAlertType, SoundName> = {
    NEW_EVENT: 'new-event-alert',
    AED_ATTEMPTING_ACCEPTING: 'aed-attempting-accepting',
    UNIT_TIMER_EXPIRED: 'unit-timer-alert',
    URGENT_BOLO_RECEIVED: 'first-responder-incoming-bolo',
    INCOMING_ASSIGNMENT: 'first-responder-incoming-msg',
    DEX_HOT_HIT: 'dex-hot-hit-alert',
    MESSAGE_RECEIVED: 'new-message-received',
    EMERGENCY: 'emergency-siren',
    DRAFT_EVENT_TIMER_EXPIRED: 'draft-event-timer-alert',
};

const LOOPING_ALERTS: ReadonlySet<AudibleAlertType> = new Set(['DRAFT_EVENT_TIMER_EXPIRED']);

const NO_SUBJECT: ConfiguredSoundSubject = { subjectType: 'NONE', subjectId: 0 };

interface AlertSound {
    triggerType: TriggerEnumType;
    subject?: ConfiguredSoundSubject;
    /** The alert's Audio Alerts preference, or true for an alert without one. */
    isLegacyPreferenceOn: boolean;
    /** Plays its default sound even when configured as "No sound". */
    isNeverSilent?: boolean;
    /** In Electron, plays in one window only rather than once per open window. */
    playsInOneWindow?: boolean;
}

function messagePreference(isCad: boolean, isUrgent: boolean): keyof AudibleAlertFeatures {
    if (isCad) {
        return isUrgent
            ? 'dispatchPlayAlertOnNewUrgentMessage'
            : 'dispatchPlayAlertOnNewStandardMessage';
    }
    return isUrgent
        ? 'firstResponderPlayAlertOnNewUrgentMessage'
        : 'firstResponderPlayAlertOnNewStandardMessage';
}

/** The trigger and subject the alert resolves against, or null when it doesn't sound here. */
function describeAlert(alert: AudibleAlert): AlertSound | null {
    switch (alert.type) {
        case 'NEW_EVENT': {
            if (!alert.context.isCad || alert.isCallTakenByCurrentUser) {
                return null;
            }
            // The AED alert takes over for an AED event only when it actually makes a sound, so a
            // silent AED alert leaves the event to the new event alert.
            const aedAlert: AudibleAlert = {
                type: 'AED_ATTEMPTING_ACCEPTING',
                context: alert.context,
                agencyTypeGlobalAttrId: alert.agencyTypeGlobalAttrId,
            };
            if (alert.isAed && resolveAlert(aedAlert) !== null) {
                return null;
            }
            return {
                triggerType: TriggerEnum.NEW_EVENT.name,
                subject: {
                    subjectType: 'EVENT_PRIORITY',
                    subjectId: alert.priorityId,
                    agencyTypeGlobalAttrId: alert.agencyTypeGlobalAttrId,
                },
                isLegacyPreferenceOn: !!alert.context.enabledFeatures
                    ?.dispatchPlayAlertOnEnterUnassignedQueue,
            };
        }
        case 'AED_ATTEMPTING_ACCEPTING':
            return {
                triggerType: TriggerEnum.AED_ATTEMPTING_ACCEPTING.name,
                subject: { ...NO_SUBJECT, agencyTypeGlobalAttrId: alert.agencyTypeGlobalAttrId },
                isLegacyPreferenceOn: !!alert.context.enabledFeatures?.dispatchPlayAlertOnAedEvent,
            };
        case 'UNIT_TIMER_EXPIRED':
            if (!alert.context.isCad) {
                return null;
            }
            return {
                triggerType: TriggerEnum.UNIT_TIMER_EXPIRED.name,
                subject: { ...NO_SUBJECT, agencyTypeGlobalAttrId: alert.agencyTypeGlobalAttrId },
                isLegacyPreferenceOn: !!alert.context.enabledFeatures
                    ?.dispatchPlayAlertOnUnitStatusTimerExpiration,
            };
        case 'URGENT_BOLO_RECEIVED':
            if (alert.context.isCad) {
                return null;
            }
            return {
                triggerType: TriggerEnum.BOLO_RECEIVED.name,
                isLegacyPreferenceOn: !!alert.context.enabledFeatures?.playAlertOnUrgentBolos,
            };
        // An AED auto-dispatch assignment has its own trigger and preference, so muting routine
        // assignments can't silence it. Both play on one channel: they're the same modal's alert.
        case 'INCOMING_ASSIGNMENT':
            return alert.isAed
                ? {
                      triggerType: TriggerEnum.AED_ASSIGNED_TO_MY_UNIT.name,
                      subject: {
                          ...NO_SUBJECT,
                          agencyTypeGlobalAttrId: alert.agencyTypeGlobalAttrId,
                      },
                      isLegacyPreferenceOn: !!alert.context.enabledFeatures
                          ?.firstResponderPlayAlertOnIncomingAedAssignment,
                  }
                : {
                      triggerType: TriggerEnum.EVENT_ASSIGNED_TO_MY_UNIT.name,
                      subject: {
                          subjectType: 'EVENT_PRIORITY',
                          subjectId: alert.priorityId,
                          agencyTypeGlobalAttrId: alert.agencyTypeGlobalAttrId,
                      },
                      isLegacyPreferenceOn: !!alert.context.enabledFeatures
                          ?.firstResponderPlayAlertOnIncomingAssignment,
                  };
        // A dispatcher hears hot hits on other users' queries; a first responder hears their own.
        case 'DEX_HOT_HIT':
            if (alert.context.isCad) {
                return alert.hasNewHotHitFromOtherUserQuery
                    ? {
                          triggerType: TriggerEnum.DEX_HOT_HIT.name,
                          isLegacyPreferenceOn: !!alert.context.enabledFeatures
                              ?.playAlertOnHotHitResultForOfficerDexQuery,
                          playsInOneWindow: true,
                      }
                    : null;
            }
            return alert.hasNewHotHitFromOwnQuery
                ? {
                      triggerType: TriggerEnum.DEX_HOT_HIT.name,
                      isLegacyPreferenceOn: !!alert.context.enabledFeatures
                          ?.firstResponderPlayAlertOnHotHitDexResult,
                  }
                : null;
        case 'MESSAGE_RECEIVED':
            return {
                triggerType: TriggerEnum.MESSAGE_RECEIVED.name,
                // Messages have no priority entity, so urgent ones match a fixed subject id.
                subject: alert.isUrgent
                    ? { subjectType: 'MESSAGE_PRIORITY', subjectId: URGENT_MESSAGE_PRIORITY_ID }
                    : undefined,
                isLegacyPreferenceOn: !!alert.context.enabledFeatures?.[
                    messagePreference(alert.context.isCad, alert.isUrgent)
                ],
                playsInOneWindow: true,
            };
        // CAD rejects a "No sound" emergency row, but one saved before it did still plays the siren.
        case 'EMERGENCY':
            return {
                triggerType: TriggerEnum.EMERGENCY.name,
                isLegacyPreferenceOn: true,
                isNeverSilent: true,
            };
        case 'DRAFT_EVENT_TIMER_EXPIRED':
            return {
                triggerType: TriggerEnum.DRAFT_EVENT_TIMER_EXPIRED.name,
                isLegacyPreferenceOn: true,
            };
    }
}

/** The sound the alert plays here, or null for none. */
function resolveAlert(
    alert: AudibleAlert
): { soundName: SoundName; playsInOneWindow: boolean } | null {
    const alertSound = describeAlert(alert);
    if (!alertSound) {
        return null;
    }
    const defaultSound = DEFAULT_SOUNDS[alert.type];
    const soundName =
        colorSoundConfigStore.resolveSound({
            triggerType: alertSound.triggerType,
            subject: alertSound.subject,
            defaultSound,
            isLegacyPreferenceOn: alertSound.isLegacyPreferenceOn,
        }) ?? (alertSound.isNeverSilent ? defaultSound : null);
    return soundName ? { soundName, playsInOneWindow: !!alertSound.playsInOneWindow } : null;
}

export function createAudibleAlerts(player: SoundPlayer): AudibleAlerts {
    return {
        playAlert(alert) {
            const channel = alert.type;
            const resolved = resolveAlert(alert);
            if (LOOPING_ALERTS.has(channel)) {
                // Re-resolved on every play, so a config change swaps the looping sound.
                if (resolved) {
                    player.startLoopingSound(resolved.soundName, channel);
                } else {
                    player.stopLoopingSound(channel);
                }
                return resolved !== null;
            }
            if (!resolved) {
                return false;
            }
            const { soundName, playsInOneWindow } = resolved;
            if (playsInOneWindow && electronBridge) {
                electronBridge
                    .requestToPlayAudioAlert()
                    .then(function playIfThisWindowPlaysAlerts(isThisWindow) {
                        if (isThisWindow) {
                            player.playSound(soundName, channel);
                        }
                    })
                    .catch(function logAudioAlertWindowCheckFailure() {
                        logErrorMessage('Could not check which window plays audio alerts', {
                            alertType: channel,
                        });
                    });
                return true;
            }
            player.playSound(soundName, channel);
            return true;
        },
        stopAlert(type) {
            if (LOOPING_ALERTS.has(type)) {
                player.stopLoopingSound(type);
            } else {
                player.stopSound(type);
            }
        },
    };
}

(Object.keys(DEFAULT_SOUNDS) as AudibleAlertType[]).forEach(function preloadDefaultSound(type) {
    preloadSound(DEFAULT_SOUNDS[type], type, { loop: LOOPING_ALERTS.has(type) });
});

export const audibleAlerts = createAudibleAlerts(soundPlayer);
